import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface Usuario {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  nivel?: 'usuario' | 'gerente' | 'admin';
  role?: string;
  empresa_id?: number;
  rua?: string;
  bairro?: string;
  numero_casa?: string;
  cep?: string;
  cidade?: string;
  estado?: string;
  endereco?: {
    rua: string;
    bairro: string;
    numero_casa: string;
    cep: string;
    cidade?: string;
    estado?: string;
  };
  enderecos?: Array<{
    id: number;
    apelido: string;
    rua: string;
    bairro: string;
    numero_casa: string;
    cep: string;
    cidade: string;
    estado: string;
  }>;
}

interface AuthContextType {
  usuario: Usuario | null;
  token: string | null;
  login: (email: string, senha: string, isAdmin?: boolean, lembrarMe?: boolean) => Promise<void>;
  registro: (dados: RegistroData) => Promise<void>;
  logout: () => void;
  recuperarSenha: (email: string) => Promise<void>;
  redefinirSenha: (token: string, novaSenha: string) => Promise<void>;
  atualizarPerfil: (dados: PerfilData) => Promise<void>;
  carregarPerfil: () => Promise<void>;
  listarEnderecos: () => Promise<void>;
  adicionarEndereco: (dados: any) => Promise<void>;
  excluirEndereco: (id: number) => Promise<void>;
  isLoading: boolean;
}

interface RegistroData {
  nome: string;
  sobrenome: string;
  email: string;
  senha: string;
  telefone: string;
  rua: string;
  bairro: string;
  numero_casa: string;
  cep: string;
}

interface PerfilData {
  nome: string;
  sobrenome: string;
  telefone: string;
  rua: string;
  bairro: string;
  numero_casa: string;
  cep: string;
  cidade?: string;
  estado?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Função para verificar se o token expirou
  const verificarExpiracaoToken = (): boolean => {
    // Verificar primeiro no localStorage, depois no sessionStorage
    let tokenExpiration = localStorage.getItem('authTokenExpiration');
    if (!tokenExpiration) {
      tokenExpiration = sessionStorage.getItem('authTokenExpiration');
    }
    
    if (!tokenExpiration) {
      return false; // Se não há data de expiração, considerar expirado
    }
    
    const expirationDate = new Date(tokenExpiration);
    const agora = new Date();
    
    // Verificar se ainda não expirou (com margem de 1 minuto)
    return expirationDate > agora;
  };

  // Função para verificar se o token está próximo de expirar (faltando menos de 1 dia)
  const verificarTokenProximoExpiracao = (): boolean => {
    // Verificar primeiro no localStorage, depois no sessionStorage
    let tokenExpiration = localStorage.getItem('authTokenExpiration');
    if (!tokenExpiration) {
      tokenExpiration = sessionStorage.getItem('authTokenExpiration');
    }
    
    if (!tokenExpiration) {
      return false;
    }
    
    const expirationDate = new Date(tokenExpiration);
    const agora = new Date();
    const umDiaEmMs = 24 * 60 * 60 * 1000; // 1 dia em milissegundos
    
    // Verificar se falta menos de 1 dia para expirar
    return (expirationDate.getTime() - agora.getTime()) < umDiaEmMs && expirationDate > agora;
  };

  // Função para salvar token com data de expiração (7 dias se lembrarMe, senão apenas sessão)
  const salvarTokenComExpiracao = (token: string, lembrarMe: boolean = true) => {
    const agora = new Date();
    const expiracao = new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 dias
    
    if (lembrarMe) {
      // Salvar no localStorage para persistir por 7 dias
      localStorage.setItem('authToken', token);
      localStorage.setItem('authTokenExpiration', expiracao.toISOString());
    } else {
      // Salvar no sessionStorage apenas para a sessão atual
      sessionStorage.setItem('authToken', token);
      sessionStorage.setItem('authTokenExpiration', expiracao.toISOString());
    }
    
    // Configurar token no axios
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  // Função para verificar se o token ainda é válido no servidor
  const verificarTokenNoServidor = async (): Promise<boolean> => {
    try {
      // Fazer uma requisição simples para verificar se o token ainda é válido
      await api.get('/api/auth/perfil');
      return true;
    } catch (error: any) {
      // Se der erro 401/403, o token não é mais válido
      if (error.response?.status === 401 || error.response?.status === 403) {
        return false;
      }
      // Para outros erros, assumir que o token ainda é válido (pode ser erro de rede)
      return true;
    }
  };

  // Carregar token e usuário do localStorage ou sessionStorage ao inicializar
  useEffect(() => {
    // Tentar carregar do localStorage primeiro, depois do sessionStorage
    let tokenSalvo = localStorage.getItem('authToken');
    let usuarioSalvo = localStorage.getItem('authUsuario');
    let storageType = 'localStorage';
    
    if (!tokenSalvo || !usuarioSalvo) {
      tokenSalvo = sessionStorage.getItem('authToken');
      usuarioSalvo = sessionStorage.getItem('authUsuario');
      storageType = 'sessionStorage';
    }

    if (tokenSalvo && usuarioSalvo) {
      try {
        // Verificar se o token ainda não expirou
        if (verificarExpiracaoToken()) {
          setToken(tokenSalvo);
          setUsuario(JSON.parse(usuarioSalvo));
          
          // Configurar token no axios para uso em requisições subsequentes
          api.defaults.headers.common['Authorization'] = `Bearer ${tokenSalvo}`;
          console.log(`✅ [AUTH-CONTEXT] Sessão restaurada do ${storageType}`);
        } else {
          // Token expirado, limpar dados
          console.log('Token expirado. Fazendo logout automático.');
          localStorage.removeItem('authToken');
          localStorage.removeItem('authUsuario');
          localStorage.removeItem('authTokenExpiration');
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('authUsuario');
          sessionStorage.removeItem('authTokenExpiration');
          delete api.defaults.headers.common['Authorization'];
        }
      } catch (error) {
        console.error('Erro ao carregar dados de autenticação:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUsuario');
        localStorage.removeItem('authTokenExpiration');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authUsuario');
        sessionStorage.removeItem('authTokenExpiration');
        delete api.defaults.headers.common['Authorization'];
      }
    }
    
    setIsLoading(false);
  }, []);


  const login = async (email: string, senha: string, isAdmin: boolean = false, lembrarMe: boolean = true) => {
    try {
      console.log('🔍 [AUTH-CONTEXT] Login chamado:', { email, senha: senha ? '***' : 'undefined', isAdmin, lembrarMe });
      
      if (isAdmin) {
        // Login como administrador (tabela users) - usar rota específica do admin
        console.log('🔍 [AUTH-CONTEXT] Fazendo POST para /api/admin/login com:', { email, senha: senha ? '***' : 'undefined' });
        const response = await api.post('/api/admin/login', { email, senha });
        const { token: novoToken, usuario: novoUsuario } = response.data;

        // Verificar se o usuário tem nível admin
        if (novoUsuario.nivel !== 'admin') {
          throw new Error('Acesso negado. Apenas administradores podem acessar esta área.');
        }

        setToken(novoToken);
        setUsuario(novoUsuario);

        // Salvar com data de expiração (7 dias se lembrarMe, senão apenas sessão)
        salvarTokenComExpiracao(novoToken, lembrarMe);
        if (lembrarMe) {
          localStorage.setItem('authUsuario', JSON.stringify(novoUsuario));
        } else {
          sessionStorage.setItem('authUsuario', JSON.stringify(novoUsuario));
        }
        
        // Redirecionar para dashboard se for admin
        if (novoUsuario.nivel === 'admin') {
          window.location.href = '/dashboard';
        }
      } else {
        // Login como cliente (tabela usuarios)
        const response = await api.post('/api/auth/login', { email, senha });
        const { token: novoToken, usuario: novoUsuario } = response.data;

        setToken(novoToken);
        setUsuario(novoUsuario);

        // Salvar com data de expiração (7 dias se lembrarMe, senão apenas sessão)
        salvarTokenComExpiracao(novoToken, lembrarMe);
        if (lembrarMe) {
          localStorage.setItem('authUsuario', JSON.stringify(novoUsuario));
        } else {
          sessionStorage.setItem('authUsuario', JSON.stringify(novoUsuario));
        }
      }
    } catch (error: any) {
      // Tratamento específico de erros
      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.erro || error.response.data?.error;
        
        if (status === 401) {
          if (isAdmin) {
            if (serverMessage?.includes('Usuário não encontrado')) {
              throw new Error('Usuário não encontrado');
            } else if (serverMessage?.includes('Senha incorreta')) {
              throw new Error('Senha incorreta');
            } else {
              throw new Error('Credenciais inválidas');
            }
          } else {
            throw new Error('Email ou senha incorretos');
          }
        } else if (status === 400) {
          throw new Error(serverMessage || 'Dados inválidos');
        } else if (status === 403) {
          throw new Error('Acesso negado. Sua conta pode estar desativada');
        } else if (status === 500) {
          throw new Error('Erro interno do servidor');
        } else if (status === 503) {
          throw new Error('Serviço temporariamente indisponível');
        } else {
          throw new Error(serverMessage || 'Erro de conexão');
        }
      } else if (error.request) {
        throw new Error('Sem conexão com o servidor');
      } else {
        throw new Error(error.message || 'Erro ao fazer login');
      }
    }
  };

  const registro = async (dados: RegistroData) => {
    try {
      const response = await api.post('/api/auth/registro', dados);
      const { token: novoToken, usuario: novoUsuario } = response.data;

      setToken(novoToken);
      setUsuario(novoUsuario);

      // Por padrão, salvar no localStorage com data de expiração (7 dias) no registro
      salvarTokenComExpiracao(novoToken, true);
      localStorage.setItem('authUsuario', JSON.stringify(novoUsuario));

      // Configurar token no axios
      api.defaults.headers.common['Authorization'] = `Bearer ${novoToken}`;
    } catch (error: any) {
      throw new Error(error.response?.data?.erro || 'Erro ao fazer registro');
    }
  };

  const logout = () => {
    setToken(null);
    setUsuario(null);
    // Limpar tanto localStorage quanto sessionStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUsuario');
    localStorage.removeItem('authTokenExpiration');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authUsuario');
    sessionStorage.removeItem('authTokenExpiration');
    // Remover token do axios
    delete api.defaults.headers.common['Authorization'];
  };

  // Verificar periodicamente se o token expirou (a cada 30 minutos)
  useEffect(() => {
    if (!token) return;

    const verificarExpiracao = setInterval(async () => {
      // Primeiro verificar se expirou no localStorage
      if (!verificarExpiracaoToken()) {
        console.log('Token expirado durante uso. Fazendo logout automático.');
        logout();
        return;
      }

      // Se está próximo de expirar, verificar no servidor se ainda é válido
      if (verificarTokenProximoExpiracao()) {
        console.log('⚠️ [AUTH-CONTEXT] Token próximo de expirar. Verificando validade no servidor...');
        const aindaValido = await verificarTokenNoServidor();
        if (!aindaValido) {
          console.log('Token não é mais válido no servidor. Fazendo logout automático.');
          logout();
        }
      }
    }, 30 * 60 * 1000); // Verificar a cada 30 minutos

    return () => clearInterval(verificarExpiracao);
  }, [token]);

  const recuperarSenha = async (email: string) => {
    try {
      await api.post('/api/auth/recuperar-senha', { email });
    } catch (error: any) {
      throw new Error(error.response?.data?.erro || 'Erro ao solicitar recuperação de senha');
    }
  };

  const redefinirSenha = async (token: string, novaSenha: string) => {
    try {
      await api.post('/api/auth/redefinir-senha', { token, nova_senha: novaSenha });
    } catch (error: any) {
      throw new Error(error.response?.data?.erro || 'Erro ao redefinir senha');
    }
  };

  const atualizarPerfil = async (dados: PerfilData) => {
    try {
      await api.put('/api/auth/perfil', dados);
      
      // Atualizar usuário local
      if (usuario) {
        const usuarioAtualizado = {
          ...usuario,
          nome: `${dados.nome} ${dados.sobrenome}`,
          telefone: dados.telefone,
          rua: dados.rua,
          bairro: dados.bairro,
          numero_casa: dados.numero_casa,
          cep: dados.cep,
          cidade: dados.cidade,
          estado: dados.estado,
          endereco: {
            rua: dados.rua,
            bairro: dados.bairro,
            numero_casa: dados.numero_casa,
            cep: dados.cep,
            cidade: dados.cidade,
            estado: dados.estado
          }
        };
        
        setUsuario(usuarioAtualizado);
        
        // Salvar no mesmo storage que foi usado no login (localStorage ou sessionStorage)
        const tokenNoLocalStorage = localStorage.getItem('authToken');
        if (tokenNoLocalStorage) {
          localStorage.setItem('authUsuario', JSON.stringify(usuarioAtualizado));
        } else {
          sessionStorage.setItem('authUsuario', JSON.stringify(usuarioAtualizado));
        }
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.erro || 'Erro ao atualizar perfil');
    }
  };

  const carregarPerfil = async () => {
    try {
      const response = await api.get('/api/auth/perfil');
      const { usuario: novoUsuario } = response.data;
      
      console.log('🔍 [AUTH-CONTEXT] Dados do perfil carregados:', novoUsuario);
      console.log('🔍 [AUTH-CONTEXT] Endereço do usuário:', {
        rua: novoUsuario.rua,
        bairro: novoUsuario.bairro,
        numero_casa: novoUsuario.numero_casa,
        cep: novoUsuario.cep,
        cidade: novoUsuario.cidade,
        estado: novoUsuario.estado,
        endereco: novoUsuario.endereco
      });
      
      // Garantir que os dados de endereço estejam disponíveis diretamente no usuário
      const usuarioComEndereco = {
        ...novoUsuario,
        rua: novoUsuario.endereco?.rua || novoUsuario.rua,
        bairro: novoUsuario.endereco?.bairro || novoUsuario.bairro,
        numero_casa: novoUsuario.endereco?.numero_casa || novoUsuario.numero_casa,
        cep: novoUsuario.endereco?.cep || novoUsuario.cep,
        cidade: novoUsuario.endereco?.cidade || novoUsuario.cidade,
        estado: novoUsuario.endereco?.estado || novoUsuario.estado || 'SC'
      };
      
      console.log('📍 Usuário com endereço processado:', usuarioComEndereco);
      
      setUsuario(usuarioComEndereco);
      
      // Salvar no mesmo storage que foi usado no login (localStorage ou sessionStorage)
      const tokenNoLocalStorage = localStorage.getItem('authToken');
      if (tokenNoLocalStorage) {
        localStorage.setItem('authUsuario', JSON.stringify(usuarioComEndereco));
      } else {
        sessionStorage.setItem('authUsuario', JSON.stringify(usuarioComEndereco));
      }
    } catch (error: any) {
      console.error('Erro ao carregar perfil:', error);
      // Se der erro de autenticação, fazer logout
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  const listarEnderecos = async () => {
    try {
      const response = await api.get('/api/auth/enderecos');
      if (usuario) {
        setUsuario({ ...usuario, enderecos: response.data.enderecos });
      }
    } catch (error) {
      console.error('Erro ao listar endereços:', error);
    }
  };

  const adicionarEndereco = async (dados: any) => {
    try {
      await api.post('/api/auth/enderecos', dados);
      await listarEnderecos();
    } catch (error: any) {
      throw new Error(error.response?.data?.erro || 'Erro ao adicionar endereço');
    }
  };

  const excluirEndereco = async (id: number) => {
    try {
       await api.delete(`/api/auth/enderecos/${id}`);
       await listarEnderecos();
    } catch (error: any) {
       throw new Error(error.response?.data?.erro || 'Erro ao excluir endereço');
    }
  };

  const value: AuthContextType = {
    usuario,
    token,
    login,
    registro,
    logout,
    recuperarSenha,
    redefinirSenha,
    atualizarPerfil,
    carregarPerfil,
    listarEnderecos,
    adicionarEndereco,
    excluirEndereco,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
